import type { MeetingGrant } from './types'

/**
 * The meeting room: many publishers, not one camera.
 *
 * Coverage's `livekit.ts` has a broadcaster and viewers, and a viewer never publishes. A
 * meeting is the other shape — everybody sends and everybody receives — so this is its own
 * machinery rather than a flag on that one.
 *
 * ## Everything is dynamically imported
 *
 * `livekit-client` is ~250 KB and importing ANY value from it at module scope pulls the lot
 * into the main bundle, including for people who never open a meeting. Types at module scope
 * are free; values live inside `connect()`. That is also why connection state is compared as
 * a string rather than against the library's `ConnectionState` enum — an enum is a value.
 *
 * ## The room object is the source of truth
 *
 * `connected` is read from the room, never latched from an event. Depending on catching one
 * event is how a room ends up connected — the server says so, the console says so — while
 * the interface sits on "Joining…" for ever.
 *
 * ## Remote audio needs an element on the page
 *
 * LiveKit does not play remote audio for you. An audio element inside a React subtree that
 * unmounts stops playing with no error: the tiles appear, the indicators light, and the room
 * is silent, which reads as a network problem. Audio elements are appended to a hidden node
 * on `document.body` and removed when the room closes.
 */

export type RoomPhase = 'idle' | 'connecting' | 'connected' | 'failed' | 'closed'

export interface Peer {
  identity: string
  name: string
  /** Null until a camera track arrives — somebody with their camera off is still here. */
  stream: MediaStream | null
  speaking: boolean
  micOn: boolean
  cameraOn: boolean
  local: boolean
}

export interface RoomSnapshot {
  phase: RoomPhase
  peers: Peer[]
  error: string | null
  micOn: boolean
  cameraOn: boolean
}

type Listener = () => void

// Module-level, like coverage's broadcast session and for the same reason: as component
// state, a panel unmounting would drop the connection and take somebody's camera off.
let room: { disconnect: () => Promise<void> } | null = null
let audioHost: HTMLDivElement | null = null
let snapshot: RoomSnapshot = {
  phase: 'idle', peers: [], error: null, micOn: true, cameraOn: true,
}
const listeners = new Set<Listener>()

export function subscribe(listener: Listener) {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

export function getSnapshot(): RoomSnapshot {
  return snapshot
}

function set(patch: Partial<RoomSnapshot>) {
  snapshot = { ...snapshot, ...patch }
  listeners.forEach((l) => l())
}

/** A device refusal in words somebody can act on, which the browser's own message is not. */
export function deviceRefusal(e: unknown): string {
  const name = (e as { name?: string } | null)?.name
  switch (name) {
    case 'NotAllowedError':
      return 'Permission was refused. Allow camera and microphone for this site, then rejoin.'
    case 'NotFoundError':
      return 'No camera or microphone was found on this device.'
    case 'NotReadableError':
      return 'The camera is already in use by another app. Close it and rejoin.'
    default:
      return (e as Error)?.message ?? 'Could not open the camera.'
  }
}

export async function connect(grant: MeetingGrant, displayName: string) {
  if (room) {
    await leave()
  }
  set({ phase: 'connecting', error: null, peers: [] })
  try {
    const { Room, RoomEvent, Track } = await import('livekit-client')
    const live = new Room({ adaptiveStream: true, dynacast: false })

    audioHost = document.createElement('div')
    audioHost.style.display = 'none'
    document.body.appendChild(audioHost)

    const sync = () => {
      const peers: Peer[] = []
      const add = (p: any, local: boolean) => {
        const camera = p.getTrackPublication?.(Track.Source.Camera)
        const mic = p.getTrackPublication?.(Track.Source.Microphone)
        const track = camera?.videoTrack ?? camera?.track
        peers.push({
          identity: p.identity,
          name: p.name || p.identity,
          stream: track?.mediaStream ?? null,
          speaking: Boolean(p.isSpeaking),
          micOn: Boolean(mic && !mic.isMuted),
          cameraOn: Boolean(camera && !camera.isMuted && track),
          local,
        })
      }
      add(live.localParticipant, true)
      live.remoteParticipants.forEach((p: any) => add(p, false))
      // Read from the ROOM, never latched from an event. See the header.
      set({ phase: String(live.state) === 'connected' ? 'connected' : snapshot.phase, peers })
    }

    live
      .on(RoomEvent.ParticipantConnected, sync)
      .on(RoomEvent.ParticipantDisconnected, sync)
      .on(RoomEvent.TrackSubscribed, (track: any, _pub: any, participant: any) => {
        if (track.kind === 'audio' && audioHost) {
          // Attached to a node outside React's tree: a remounting subtree would otherwise
          // silence the room with no error anywhere.
          const el = track.attach()
          el.dataset.identity = participant.identity
          audioHost.appendChild(el)
        }
        sync()
      })
      .on(RoomEvent.TrackUnsubscribed, (track: any) => {
        track.detach().forEach((el: HTMLMediaElement) => el.remove())
        sync()
      })
      .on(RoomEvent.TrackMuted, sync)
      .on(RoomEvent.TrackUnmuted, sync)
      .on(RoomEvent.ActiveSpeakersChanged, sync)
      .on(RoomEvent.LocalTrackPublished, sync)
      .on(RoomEvent.Reconnecting, () => set({ phase: 'connecting' }))
      .on(RoomEvent.Reconnected, sync)
      .on(RoomEvent.Disconnected, () => {
        // Force-ended by the host, token expired, or the meeting was closed. Release the
        // camera: leaving it lit under a panel that says you have left was a bug in the
        // feature this borrows from.
        void teardown()
        set({ phase: 'closed', peers: [] })
      })

    await live.connect(grant.wsUrl, grant.token)
    room = live

    /*
      H.264, not the library's VP8 default, and one encoding.

      Recording is not built yet and this still matters: the recording will be an MP4, MP4
      cannot carry VP8, and the failure is silent — the meeting looks perfect and the file is
      empty. Coverage lost a broadcast exactly that way. Simulcast off for the same family of
      reason: egress picks a layer, and the layer it picks may not be the one being sent.
    */
    /*
      A DISPLAY NAME IS COSMETIC AND MUST NEVER UNDO A JOIN.

      This sat in the outer try, so when LiveKit refused it — "update own metadata not
      allowed", because the token lacked canUpdateOwnMetadata — the shared catch tore down a
      connection that had already succeeded, and the person was shown a permissions error
      about metadata they never asked to change. The grant is fixed on the server, but the
      client must not depend on it: the name is already carried in the token, so failing to
      set it again changes nothing anybody can see.
    */
    try {
      await live.localParticipant.setName?.(displayName)
    } catch {
      // Deliberately swallowed. The token's name stands.
    }

    /*
      THE CAMERA IS NOT ALLOWED TO UNDO THE JOIN.

      This used to sit inside the same try as `live.connect`, so a device failure after a
      SUCCESSFUL connection ran the shared catch: teardown, phase 'failed', and the
      participant dropped out of a room they were already in. The guest flow made that
      visible — the chair's panel listed the guest as present (they were: LiveKit had them)
      while the guest's own screen fell back to the "Ask to join" form with nothing to
      explain it. A ward coordinator on a borrowed laptop with no webcam, or anybody who
      declines the permission prompt, hits this every time and cannot get past it.

      Connection and devices are separate concerns, so they get separate failure handling.
      A person who cannot open a camera should still be IN the meeting: they can hear, read
      the chat, raise a hand, and be seen in the roster. The error is recorded for the UI to
      show, but the phase stays whatever the room says it is.
    */
    try {
      await live.localParticipant.enableCameraAndMicrophone()
      sync()
      set({ micOn: true, cameraOn: true, error: null })
    } catch (deviceError) {
      sync()
      // Not 'failed' — the room is live. Muted because nothing was ever opened, so the
      // controls must not claim otherwise.
      set({ micOn: false, cameraOn: false, error: deviceRefusal(deviceError) })
    }
  } catch (e) {
    await teardown()
    set({ phase: 'failed', error: deviceRefusal(e) })
  }
}

export async function toggleMic() {
  const live = room as any
  if (!live) return
  const next = !snapshot.micOn
  try {
    await live.localParticipant.setMicrophoneEnabled(next)
    set({ micOn: next })
  } catch (e) {
    // A catch, because for months the equivalent control in the system this borrows from had
    // none: a device refusal was an unhandled rejection, so the button did nothing and said
    // nothing.
    set({ error: deviceRefusal(e) })
  }
}

export async function toggleCamera() {
  const live = room as any
  if (!live) return
  const next = !snapshot.cameraOn
  try {
    await live.localParticipant.setCameraEnabled(next)
    set({ cameraOn: next })
  } catch (e) {
    set({ error: deviceRefusal(e) })
  }
}

export async function leave() {
  await teardown()
  set({ phase: 'idle', peers: [], error: null })
}

async function teardown() {
  const live = room
  room = null
  try {
    await live?.disconnect()
  } catch { /* already gone */ }
  audioHost?.remove()
  audioHost = null
}

/**
 * Closing the tab IS leaving the meeting, and it should be a clean exit.
 *
 * `pagehide` rather than `beforeunload`: the latter does not fire reliably on mobile Safari.
 * A clean disconnect makes the server's `participant_left` webhook fire now rather than after
 * a timeout, so the roster stops showing somebody who has gone.
 */
if (typeof window !== 'undefined') {
  window.addEventListener('pagehide', () => { void teardown() })
}
